import { SubscriptionPlanDto, SubscriptionType } from '@tailored-pdf/api';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const CURRENCY_LABEL = '$';

export const BASE_TARIFF = SubscriptionType.TRIAL;

export function useBillingInfoGetter() {
  const { t } = useTranslation('web', { keyPrefix: 'tariffs.billingInfo' });

  return useCallback(
    (tariff: SubscriptionType, plans?: SubscriptionPlanDto[]) => {
      const trialPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.TRIAL);
      const monthPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.MONTHLY);
      const yearPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.YEARLY);

      switch (tariff) {
        case SubscriptionType.FREE:
          return t('FREE');
        case SubscriptionType.TRIAL:
          return trialPlan && monthPlan
            ? t('TRIAL', { currency: CURRENCY_LABEL, initialCharge: trialPlan.price, charge: monthPlan.price })
            : null;
        case SubscriptionType.MONTHLY:
          return monthPlan ? t('MONTHLY', { currency: CURRENCY_LABEL, initialCharge: monthPlan.price }) : null;
        case SubscriptionType.YEARLY:
          return yearPlan ? t('YEARLY', { currency: CURRENCY_LABEL, initialCharge: yearPlan.price }) : null;
        default:
          return null;
      }
    },
    [t],
  );
}

export function getBaseCost(plan: SubscriptionPlanDto) {
  switch (plan.subscriptionType) {
    case SubscriptionType.FREE:
      return 0;
    case SubscriptionType.TRIAL:
      return plan.price;
    case SubscriptionType.MONTHLY:
      return Math.round((plan.price / 4) * 100) / 100;
    case SubscriptionType.YEARLY:
      return Math.round((plan.price / 12 / 4) * 100) / 100;
    default:
      return undefined;
  }
}

export function calcDiscount(tariff: SubscriptionType) {
  if (tariff !== SubscriptionType.YEARLY) {
    return 0;
  }
  return 83;
}

export enum TARIFF_ACTIONS {
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
}

export function getTariffAction(tariff: SubscriptionType, plans: SubscriptionPlanDto[]) {
  const currentTariff = plans.find((v) => v.active);
  if (!currentTariff || currentTariff.subscriptionType === tariff) {
    return undefined;
  }

  if (currentTariff.subscriptionType === SubscriptionType.YEARLY) {
    return TARIFF_ACTIONS.DOWNGRADE;
  }

  if (currentTariff.subscriptionType === SubscriptionType.MONTHLY) {
    if (tariff === SubscriptionType.YEARLY) {
      return TARIFF_ACTIONS.UPGRADE;
    } else {
      return TARIFF_ACTIONS.DOWNGRADE;
    }
  }

  if (currentTariff.subscriptionType === SubscriptionType.TRIAL) {
    if (tariff === SubscriptionType.FREE) {
      return TARIFF_ACTIONS.DOWNGRADE;
    } else {
      return TARIFF_ACTIONS.UPGRADE;
    }
  }

  return TARIFF_ACTIONS.UPGRADE;
}

export function useButtonLabelGetter() {
  const { t } = useTranslation('web', { keyPrefix: 'tariffs.action' });

  return useCallback(
    (tariff?: SubscriptionType, plans?: SubscriptionPlanDto[]) => {
      if (!plans || !tariff) {
        return t('blank');
      }

      const action = getTariffAction(tariff, plans);

      if (action === TARIFF_ACTIONS.DOWNGRADE) {
        return t('downgrade');
      } else if (action === TARIFF_ACTIONS.UPGRADE) {
        return t('upgrade');
      } else {
        return t('current');
      }
    },
    [t],
  );
}
